import React, { useState } from 'react';
import { format } from 'date-fns';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  Grid,
} from '@mui/material';
import Autocomplete from '@mui/material/Autocomplete';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
// components
import { useSnackbar } from 'src/components/snackbar';
import { useServerRequest } from 'src/hooks/useServerRequest';
import { formatRib } from 'src/utils/format/format_rib';
import { Company } from 'src/types/data';
import { getBeneficiairesFromReceivedChecks } from 'src/utils/checks/get-benefeceires';
import { useLocales } from 'src/locales';
import { useSelector, useDispatch } from 'react-redux';
import { selectFilteredData } from 'src/redux/selectors';
import { addReceivedCheckToRib } from 'src/redux/slices/dataSlice';

interface CheckDialogProps {
  open: boolean;
  onClose: () => void;
}

const ReceivedCheck: React.FC<CheckDialogProps> = ({ open, onClose }) => {
  const filteredData = useSelector(selectFilteredData);
  const [company, setCompany] = useState<number | null>(null);
  const [type, setType] = useState<number | null>(null);
  const [rib, setRib] = useState<number | null>(null);
  const [check, setCheck] = useState<string | null>('');
  const [beneficiary, setBeneficiary] = useState<string>('');
  const [amount, setAmount] = useState<string>('');
  const [takenDate, setTakenDate] = useState<Date | null>(new Date());
  const [dueDate, setDueDate] = useState<Date | null>(null);
  const [bill, setBill] = useState<string>('');
  const [note, setNote] = useState<string>('');
  const { t } = useLocales();
  const [ribOptions, setRibOptions] = useState<{ id: number, label: string }[]>([]);
  const { enqueueSnackbar } = useSnackbar();
  const { makeRequest } = useServerRequest();
  const listBenefecieries = getBeneficiairesFromReceivedChecks(filteredData);
  const dispatch = useDispatch();

  const handleCompanyChange = (event: any, newValue: { id: number; label: string } | null) => {
    setCompany(newValue?.id || null);

    if (newValue) {
      const selectedCompany = filteredData.find(comp => comp.id === newValue.id);

      if (selectedCompany) {
        const ribs = selectedCompany.ribs.map(rb => ({
          id: rb.id,
          label: `${formatRib(rb.rib)} - ${rb.bref}`,
        }));

        setRibOptions(ribs);
      } else {
        setRibOptions([]);
      }
    } else {
      setRibOptions([]);
    }
  };

  const handleRibChange = (event: any, newValue: { id: number; label: string } | null) => {
    setRib(newValue?.id || null);
  };

  const handleSubmit = async () => {
    if (!rib || !company) return;
    const formattedTakenDate = takenDate ? format(takenDate, 'yyyy-MM-dd HH:mm:ss') : null;
    const formattedDueDate = dueDate ? format(dueDate, 'yyyy-MM-dd HH:mm:ss') : null;

    const data = {
      type,
      rib_id: rib,
      code: check,
      benificier_id: beneficiary,
      amount,
      taken_date: formattedTakenDate,
      due_date: formattedDueDate,
      facture: bill,
      remarque: note,
    };
    if (!data.type) {
      enqueueSnackbar(t('common.input.company.error1'), { variant: 'error' });
      return;
    }
    if (!data.rib_id) {
      enqueueSnackbar(t('common.input.rib.error2'), { variant: 'error' });
      return;
    }

    if (!data.benificier_id) {
      enqueueSnackbar(t('common.input.benef.error1'), { variant: 'error' });
      return;
    }
    if (!data.amount || Number.isNaN(Number(data.amount))) {
      enqueueSnackbar(t('common.input.amount.error2'), { variant: 'error' });
      return;
    }
    if (!data.taken_date) {
      enqueueSnackbar('Taken Date is required', { variant: 'error' });
      return;
    }

    const response = await makeRequest(`/saveCheckRecu`, {
      method: 'POST',
      data,
    }, t('common.alert.check'));

    if (response.success && response.data) {
      dispatch(addReceivedCheckToRib({ companyId: company, ribId: rib, receivedCheck: response.data.check }))
      onClose();
    }
  };

  const handleAmountChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { value } = e.target;
    const formattedValue = value.replace(',', '.');
    setAmount(formattedValue);
  };

  const typeOptions = [
    {
      "id": 1,
      "label": "Check"
    },
    {
      "id": 2,
      "label": "Effect"
    }
  ];

  const companyOptions = filteredData
    .filter((comp: Company) => comp.user_role !== 'analysis')
    .map((comp: Company) => ({
      id: comp.id,
      label: comp.name,
    }));

  return (
    <Dialog disableEscapeKeyDown fullWidth maxWidth="md" open={open} onClose={(_, reason) => reason !== 'backdropClick' && onClose()}>
      <DialogTitle>{t('m.t.check')}</DialogTitle>
      <DialogContent>
        <Grid container spacing={2} sx={{ pt: '10px' }}>
          <Grid item xs={6} md={4}>
            <Autocomplete
              options={typeOptions}
              getOptionLabel={(option) => option.label || ''}
              value={type ? typeOptions.find(comp => comp.id === type) : null}
              onChange={(event, newValue) => setType(newValue?.id || null)}
              renderInput={(params) => <TextField {...params} label={t('common.input.type.select')} />}
            />
          </Grid>
          <Grid item xs={6} md={4}>
            <Autocomplete
              options={companyOptions}
              getOptionLabel={(option) => option.label || ''}
              value={company ? companyOptions.find(comp => comp.id === company) : null}
              onChange={handleCompanyChange}
              renderInput={(params) => <TextField {...params} label={t('common.input.company.select')} />}
            />
          </Grid>
          <Grid item xs={6} md={4}>
            <Autocomplete
              options={ribOptions}
              getOptionLabel={(option) => option.label || ''}
              isOptionEqualToValue={(option, value) => option.id === value?.id} // Updated comparison logic
              value={rib !== null ? ribOptions.find(rb => rb.id === rib) : null}
              onChange={handleRibChange}
              renderInput={(params) => <TextField {...params} label={t('common.input.rib.select')} />}
            />
          </Grid>

          <Grid item xs={6} md={4}>
            <TextField
              fullWidth
              label={t('common.input.checkCode.label')}
              value={check || ''}
              onChange={(e) => setCheck(e.target.value.toUpperCase())}
            />
          </Grid>
          <Grid item xs={6} md={4}>
            <Autocomplete
              fullWidth
              freeSolo
              options={listBenefecieries} // List of beneficiaries
              getOptionLabel={(option) => option} // Returns the option as a string
              value={beneficiary || ''} // Current beneficiary value
              onInputChange={(event, newValue) => setBeneficiary(newValue.toUpperCase())} // Handle input changes
              renderInput={(params) => <TextField {...params} label={t('common.input.benef.label')} />} // TextField as the input
              renderOption={(props, option) => (
                <li {...props} key={option}>
                  {option}
                </li>
              )}
            />
          </Grid>
          <Grid item xs={6} md={4}>
            <TextField
              fullWidth
              label={t('common.input.amount.label')}
              value={amount || ''}
              onChange={handleAmountChange}
            />
          </Grid>
          <Grid item xs={6} md={3}>
            <DatePicker
              label={t('common.input.takenDate.label')}
              value={takenDate || null}
              onChange={(newValue) => setTakenDate(newValue)}
              slotProps={{ textField: { fullWidth: true } }}
            />
          </Grid>

          <Grid item xs={6} md={3}>
            <DatePicker
              label={t('common.input.dueDate.label')}
              value={dueDate || null}
              onChange={(newValue) => setDueDate(newValue)}
              slotProps={{ textField: { fullWidth: true } }}
            />
          </Grid>
          <Grid item xs={12} md={6}>
            <TextField
              fullWidth
              label={t('common.input.bill.label')}
              value={bill || ''}
              onChange={(e) => setBill(e.target.value)}
            />
          </Grid>
          <Grid item xs={12}>
            <TextField
              fullWidth
              label={t('common.input.note.label')}
              multiline
              rows={2}
              value={note || ''}
              onChange={(e) => setNote(e.target.value)}
            />
          </Grid>
        </Grid>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} variant="outlined" color="inherit">
          {t('common.button.action.cancel')}
        </Button>
        <Button onClick={handleSubmit} variant="contained">
          {t('common.button.action.submit')}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default ReceivedCheck;
